import { createAsyncThunk } from "@reduxjs/toolkit";
import { post, get, put } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { MfaLoginAPI } from "../../Services/AuthServices";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { LEGACY_SESSION_KEYS, STORAGE_KEYS } from "../../constants";
import { setLoading } from "../Slices/apiStatusSlice";
import { updateAccessToken } from "../Slices/SessionSlice";

/**
 * Pull optional new JWTs from PUT /users/me/password response (supports several API shapes).
 */
function extractPasswordChangeTokens(apiBody) {
  if (apiBody == null || typeof apiBody !== "object") {
    return { accessToken: null, refreshToken: null };
  }
  const payload =
    apiBody.success === true && apiBody.data != null && typeof apiBody.data === "object"
      ? apiBody.data
      : apiBody;
  const inner =
    payload != null &&
    typeof payload === "object" &&
    payload.data != null &&
    typeof payload.data === "object" &&
    !Array.isArray(payload.data)
      ? payload.data
      : payload;
  if (inner == null || typeof inner !== "object" || Array.isArray(inner)) {
    return { accessToken: null, refreshToken: null };
  }
  return {
    accessToken: inner.accessToken ?? inner.access_token ?? null,
    refreshToken: inner.refreshToken ?? inner.refresh_token ?? null,
  };
}

/**
 * After a successful forced password change: merge returned tokens (if any) into stored auth,
 * clear `requirePasswordChange`, and sync session slice so the next GET /users/me uses the new JWT.
 * @param {Function} dispatch Redux `dispatch` from the thunk API (session slice sync).
 */
function persistAuthStorageAfterPasswordChange(apiBody, dispatch) {
  const { accessToken, refreshToken } = extractPasswordChangeTokens(apiBody);
  try {
    const raw = localStorage.getItem("authToken");
    if (!raw) return;
    const tokenData = JSON.parse(raw);
    if (typeof tokenData !== "object" || tokenData == null) return;
    const next = { ...tokenData };
    delete next.requirePasswordChange;
    if (accessToken) {
      next.accessToken = accessToken;
      next.access_token = accessToken;
    }
    if (refreshToken) {
      next.refreshToken = refreshToken;
      next.refresh_token = refreshToken;
    }
    localStorage.setItem("authToken", JSON.stringify(next));
    if (accessToken && typeof dispatch === "function") {
      dispatch(updateAccessToken(accessToken));
    }
  } catch {
    /* ignore */
  }
}

/**
 * Login user
 * API: POST /auth/login
 * Body: { email, password }
 * Response: { success: true, data: { accessToken, refreshToken } }
 */
export const login = createAsyncThunk(
  "auth/login",
  async ({ data, buttonKey: _buttonKey }, { rejectWithValue }) => {
    try {
      const payload = {
        email: data.Email || data.email,
        password: data.Password || data.password,
      };
      const response = await post(API_ENDPOINTS.AUTH.LOGIN, payload);
      const apiResponse = response.data;
      // Real API: { success: true, data: { accessToken, refreshToken } }
      if (apiResponse?.success && apiResponse?.data) {
        return apiResponse.data;
      }
      // Mock or legacy: { accessToken, refreshToken, ... }
      if (apiResponse?.accessToken) {
        return apiResponse;
      }
      return rejectWithValue(apiResponse || { message: "Login failed" });
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Login failed" });
    }
  }
);

/**
 * Force password change (first login) – PUT /users/me/password
 * Body: { password }
 */
export const updateMePassword = createAsyncThunk(
  "auth/updateMePassword",
  async (password, { rejectWithValue, dispatch }) => {
    try {
      const response = await put(API_ENDPOINTS.USERS.ME_PASSWORD, { password });
      const body = response.data;
      if (body != null) {
        persistAuthStorageAfterPasswordChange(body, dispatch);
      }
      return body;
    } catch (error) {
      const msg = error.response?.data?.message || error.message || "Failed to update password";
      return rejectWithValue(msg);
    }
  }
);

/**
 * Current user profile — GET /users/me (Bearer). Call after login; persists role id only (localStorage).
 */
export const fetchMe = createAsyncThunk("auth/fetchMe", async (_, { rejectWithValue }) => {
  try {
    const response = await get(API_ENDPOINTS.USERS.ME);
    const body = response.data;
    let data;
    if (body?.success === true) {
      data = body.data;
    } else {
      data = body?.data ?? body;
    }
    if (data == null || typeof data !== "object" || Array.isArray(data)) {
      return rejectWithValue({ message: "Invalid profile response" });
    }
    try {
      const role = data.role;
      const roleId =
        role != null && typeof role === "object" && !Array.isArray(role) ? role.id : undefined;
      if (roleId != null && roleId !== "") {
        localStorage.setItem(STORAGE_KEYS.USER_ROLE_ID, String(roleId));
      } else {
        localStorage.removeItem(STORAGE_KEYS.USER_ROLE_ID);
      }
      try {
        sessionStorage.removeItem(LEGACY_SESSION_KEYS.USER_ROLE_JSON);
      } catch {
        /* ignore */
      }
    } catch {
      /* ignore quota / private mode */
    }
    return data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error, "Failed to load user profile"));
  }
});

/**
 * MFA code verification — POST /auth/mfa/login
 * Body: { userId, mfa_code, method }
 * On success returns { accessToken, refreshToken }
 */
export const MFALoginPost = createAsyncThunk(
  "auth/mfa-login",
  async ({ data, buttonKey }, { dispatch, rejectWithValue }) => {
    dispatch(setLoading({ key: buttonKey, value: true }));
    try {
      const response = await MfaLoginAPI(data);
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "MFA verification failed" });
    } finally {
      dispatch(setLoading({ key: buttonKey, value: false }));
    }
  }
);
